import {
  useInfiniteQuery,
  useMutation,
  useMutationState,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { getUser } from "../../../selectors/commonSelectors";
import { isDefinitiveClientError } from "../../../api/error-classification";
import {
  isFullLibrary,
  UserWithPlan,
} from "../../../features/media-library/is-library-restricted";
import {
  getCapabilityFields,
  getCatalog,
  getConnection,
  getConnectionRuns,
  listResources,
  describeCapabilitySchema,
  saveCapabilityConfig,
  pauseConnection,
  resumeConnection,
  triggerConnectionSync,
  reimportIdeas,
  reimportLibraryFolder,
  disconnectConnection,
  connectRssSilently,
  validateRssFeed,
  CatalogProvider,
  RssConnectResult,
  SaveCapabilityConfigResult,
  ValidateRssFeedResult,
} from "../../../api/integrations";
import { getGroups } from "../../../api/accountGroups";
import { getContentLabels } from "../../../api/content-labels";
import { getCollections } from "../../../api/media-library/get-collections";
import { serverErrorMessage } from "../../../api/server-error-message";
import { getImpersonation } from "../../../api/impersonation-state";

// The server refuses live provider lookups during support access (SKE-5103). Fail
// in the browser instead, so no doomed request, retry or audit row is created.
const unavailableDuringSupport = () =>
  Promise.reject(new Error("Unavailable during support access"));

// react-query hooks for the external-integrations UI. Colocated per the
// ga4-property-settings convention (bare axios inside api/integrations.ts,
// toast for feedback). `integrations` is the shared query-key root.

export const integrationsQueryKeys = {
  all: ["integrations"] as const,
  catalog: () => [...integrationsQueryKeys.all, "catalog"] as const,
  connection: (id: string) =>
    [...integrationsQueryKeys.all, "connection", id] as const,
  runs: (id: string) =>
    [...integrationsQueryKeys.all, "connection", id, "runs"] as const,
  // Mutation key for a connection's config saves, so the detail view can observe the
  // latest SaveCapabilityConfigResult (via useMutationState) and surface a
  // `synced:false` outcome as an actionable alert without threading state through the
  // form components (SKE-3941).
  saveConfig: (id: string) =>
    [...integrationsQueryKeys.all, "connection", id, "saveConfig"] as const,
};

const RUNS_PAGE_SIZE = 20;

// How often to re-poll the overview while any connection is mid-transition. Matches
// the detail view's cadence so a card and its detail advance in lock-step.
const TRANSIENT_POLL_MS = 5000;

// A connection is "transient" — worth polling for — when it is actively moving
// toward a settled state, so the overview reflects the change without a manual
// refresh (SKE-3941):
//   - `syncing`        — settling to `active` on the first ok run;
//   - `pending_config` — a save may flip it to `syncing` at any moment;
//   - in-flight run     — a queued/running row exists (activity present);
//   - `sync_failing`    — errored but its access is fine and we keep retrying.
// A steady `active`/`paused`/`disconnected`/credential-`error` connection is NOT
// transient, so a settled dashboard stops polling.
export function connectionIsTransient(
  connection: CatalogProvider["connection"]
): boolean {
  if (!connection) {
    return false;
  }
  if (
    connection.status === "syncing" ||
    connection.status === "pending_config"
  ) {
    return true;
  }
  if (connection.activity) {
    return true;
  }
  if (
    connection.status === "error" &&
    connection.error?.code === "sync_failing"
  ) {
    return true;
  }
  return false;
}

export function catalogHasTransient(
  providers: CatalogProvider[] | undefined
): boolean {
  return Boolean(providers?.some((p) => connectionIsTransient(p.connection)));
}

// Cursor-paginated run history for the activity feed. Newest-first; each page's
// `nextCursor` (ISO `startedAt`) is fed back as `before` for the next (older)
// page. Polls on the same cadence as the connection detail so a freshly-triggered
// sync surfaces without a manual refresh.
export function useConnectionRuns(id: string | null) {
  return useInfiniteQuery({
    queryKey: integrationsQueryKeys.runs(id ?? ""),
    queryFn: ({ pageParam }) =>
      getConnectionRuns(id as string, {
        limit: RUNS_PAGE_SIZE,
        before: pageParam ?? undefined,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: Boolean(id),
    refetchInterval: 5000,
  });
}

export function useIntegrationsCatalog(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: integrationsQueryKeys.catalog(),
    queryFn: getCatalog,
    // Callers outside Settings (e.g. the inbox escalate modal) gate the fetch on
    // the org's entitlement so we don't fire a guaranteed 403 for orgs without
    // external integrations. Defaults on so existing call-sites are unchanged.
    enabled: options?.enabled ?? true,
    retry: (failureCount, error) =>
      failureCount < 1 && !isDefinitiveClientError(error),
    // Poll ONLY while something is mid-transition (syncing / pending / recovering),
    // so a save's status change surfaces live; a fully settled dashboard is idle and
    // makes no extra requests (SKE-3941). `refetchOnWindowFocus` is off globally.
    refetchInterval: (query) =>
      catalogHasTransient(query.state.data as CatalogProvider[] | undefined)
        ? TRANSIENT_POLL_MS
        : false,
  });
}

export function useIntegrationConnection(id: string | null) {
  return useQuery({
    queryKey: integrationsQueryKeys.connection(id ?? ""),
    queryFn: () => getConnection(id as string),
    enabled: Boolean(id),
    // Connection status advances via webhooks; poll while the detail view is open.
    refetchInterval: 5000,
  });
}

// A single {id,name} row for the local-to-Sked pickers (brand groups, content
// collections). Distinct from the provider `resources` action output, which the
// forms normalise separately.
export interface PickerOption {
  id: string;
  name: string;
}

export interface BrandGroupPickerOption extends PickerOption {
  accounts: Array<{
    id: string;
    name: string;
    avatar?: string | null;
    platformType?: string | null;
    color?: string | null;
  }>;
}

// The provider's selectable resources for a capability (projects/folders/boards),
// fetched via the /resources action. Cached for the session so re-opening the
// config form doesn't re-hit the provider; the server itself also caches. `input`
// drives the folder-picker drill-down (Drive `{parentId}` / Dropbox `{path}`); it
// is part of the query key so each tree level caches independently.
export function useCapabilityResources(
  connectionId: string,
  capability: string,
  input?: unknown
) {
  return useQuery({
    queryKey: [
      ...integrationsQueryKeys.connection(connectionId),
      "resources",
      capability,
      input ?? null,
    ] as const,
    queryFn: getImpersonation()
      ? unavailableDuringSupport
      : () => listResources(connectionId, capability, input),
    ...(getImpersonation() ? { retry: false } : {}),
    staleTime: 5 * 60 * 1000,
  });
}

// The capability's field inventory (source + target fields + per-provider default
// mapping) that the ideas mapping editor renders from. Keyed by the capability key
// plus an optional `provider` scope (RSS has its own source inventory — SKE-3995),
// independent of the connection. Cached like the resource lists — the inventory is
// static config, not per-connection state.
export function useCapabilityFields(capability: string, provider?: string) {
  return useQuery({
    queryKey: [
      ...integrationsQueryKeys.all,
      "fields",
      capability,
      provider ?? null,
    ] as const,
    queryFn: () => getCapabilityFields(capability, provider),
    staleTime: 5 * 60 * 1000,
  });
}

// A SELECTED resource's dynamic schema (its columns + status options) for the ideas
// mapping editor's per-resource sub-section (SKE-3985). Keyed by the connection,
// capability, and resource id so each selected board/database/list/sheet caches
// independently. Disabled until a resource id is present. Cached like the resource
// lists — the schema is config-time data, not per-run state, and the server itself
// short-caches it. A provider without dynamic discovery resolves to empty columns.
export function useCapabilitySchema(
  connectionId: string,
  capability: string,
  resourceId: string | null,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: [
      ...integrationsQueryKeys.connection(connectionId),
      "schema",
      capability,
      resourceId ?? null,
    ] as const,
    queryFn: getImpersonation()
      ? unavailableDuringSupport
      : () =>
          describeCapabilitySchema(
            connectionId,
            capability,
            resourceId as string
          ),
    ...(getImpersonation() ? { retry: false } : {}),
    enabled: Boolean(resourceId) && (options?.enabled ?? true),
    staleTime: 5 * 60 * 1000,
  });
}

// The content labels of a brand group, for the RSS feed's label picker (SKE-3995).
// Same self-contained react-query rationale as useBrandGroups: the picker owns its
// own loading/empty states and re-fetches when the chosen group changes. Disabled
// until a group is chosen (an RSS feed always targets one group first).
export function useGroupContentLabels(groupId: string | null) {
  return useQuery({
    queryKey: ["integrations-config", "content-labels", groupId] as const,
    queryFn: () => getContentLabels(groupId as string),
    enabled: Boolean(groupId),
    staleTime: 5 * 60 * 1000,
  });
}

// The user's brand groups (AccountGroup), reused for the ideas-source target-group
// picker — the same list the accounts page and GA4 connect modal read. Fetched via
// react-query (not the redux slice) so the picker is self-contained and gets its
// own loading/error/retry states regardless of whether the groups slice happens to
// be hydrated on the settings route.
export function useBrandGroups() {
  return useQuery({
    queryKey: ["integrations-config", "brand-groups"] as const,
    queryFn: async (): Promise<BrandGroupPickerOption[]> => {
      const { data } = await getGroups();
      return (data?.items ?? []).map((group) => ({
        id: group._id,
        name: group.name,
        accounts: group.accounts.map((account) => ({
          id: account._id,
          name: account.loginAlias || account.login || "Social account",
          avatar: account.profilePictureUrl,
          platformType: account.platformType,
          color: account.color,
        })),
      }));
    },
    staleTime: 5 * 60 * 1000,
  });
}

// The ceSDK editor's own uploads collection is system-managed and never a valid
// sync target — the media library filters it out of every listing (see
// utils/cesdk-editor-collection.ts). Inlined (not imported) to avoid pulling the
// heavy cesdk-editor module into the settings bundle.
const EDITOR_UPLOADS_INTEGRATION_TYPE = "cesdk-editor-uploads";

// The org's content-inbox collections, reused for the library-source target
// collection picker (`getCollections` → GET /content-inbox/inbox — the same list
// the media library shows). A collection's `_id` is the `targetCollectionId` a
// library sync lands assets in. Because that target is a WRITE destination, we
// only surface collections a sync can actually write into:
//   - drop the Editor Uploads collection (system-managed by the ceSDK editor) and
//     restrict to system inboxes for non-full-library users — mirroring the media
//     library's own visibility filter (selectors/mediaLibrarySelectors.getCollections);
//   - additionally drop integration-managed collections (`isManagedCollection`,
//     e.g. Frontify), which the app renders read-only (upload disabled) — picking
//     one would be a target the sync can't write to.
// `isFullLibrary` is keyed into the query so a plan change re-derives the list.
// Same self-contained react-query rationale as the brand-group picker above.
export function useContentCollections() {
  const user = useSelector(getUser) as UserWithPlan | undefined;
  const fullLibrary = isFullLibrary(user);
  return useQuery({
    queryKey: [
      "integrations-config",
      "content-collections",
      fullLibrary,
    ] as const,
    queryFn: async (): Promise<PickerOption[]> => {
      const collections = await getCollections();
      return collections
        .filter(
          (collection) =>
            collection.integrationType !== EDITOR_UPLOADS_INTEGRATION_TYPE
        )
        .filter((collection) => !collection.isManagedCollection)
        .filter((collection) => fullLibrary || collection.isSystemInbox)
        .map((collection) => ({
          id: collection._id,
          name: collection.name,
        }));
    },
    staleTime: 5 * 60 * 1000,
  });
}

function useInvalidateIntegrations() {
  const queryClient = useQueryClient();
  return (connectionId?: string) => {
    queryClient.invalidateQueries({
      queryKey: integrationsQueryKeys.catalog(),
    });
    if (connectionId) {
      queryClient.invalidateQueries({
        queryKey: integrationsQueryKeys.connection(connectionId),
      });
      queryClient.invalidateQueries({
        queryKey: integrationsQueryKeys.runs(connectionId),
      });
    }
  };
}

export function useSaveCapabilityConfig(connectionId: string) {
  const invalidate = useInvalidateIntegrations();
  return useMutation({
    // Keyed so the detail view can observe the latest result (useLatestSaveResult).
    mutationKey: integrationsQueryKeys.saveConfig(connectionId),
    mutationFn: (vars: { capability: string; config: unknown }) =>
      saveCapabilityConfig(connectionId, vars.capability, vars.config),
    onSuccess: (result) => {
      invalidate(connectionId);
      // Branch on the discriminated result so a swallowed sync-start no longer reads
      // as a plain success (SKE-3941). The connection detail carries the actionable
      // Retry / Reconnect affordance; the toast just names what happened.
      if (result.synced) {
        toast.success("Saved — sync started.");
      } else if (result.activated) {
        // Destination (action-only): no sync to start; saving completes setup and the
        // connection is now active, ready to escalate conversations.
        toast.success("Saved — ready to escalate conversations.");
      } else if (result.reason === "no_connection") {
        toast.error(
          "Saved, but there's no connection to sync yet — reconnect to start syncing."
        );
      } else {
        toast.error(
          "Saved, but the sync didn't start. Retry it from the connection below."
        );
      }
    },
    onError: (err) =>
      toast.error(serverErrorMessage(err, "Couldn't save the configuration.")),
  });
}

// The most recent config-save outcome for a connection, read from the mutation cache
// so the detail view can react to a `synced:false` save without owning the mutation
// (that lives in the capability forms). Returns `undefined` until the first save
// resolves; the newest successful result wins when several capabilities were saved.
export function useLatestSaveResult(
  connectionId: string
): SaveCapabilityConfigResult | undefined {
  const results = useMutationState({
    filters: {
      mutationKey: integrationsQueryKeys.saveConfig(connectionId),
      status: "success",
    },
    select: (mutation) => mutation.state.data as SaveCapabilityConfigResult,
  });
  return results.at(-1);
}

// Silent RSS connect (SKE-3995): no Nango Connect UI — POST the rss/connect endpoint
// to create/attach the connection, then AWAIT the catalog refetch so the caller can
// navigate straight to the (now-resolvable) provider detail without the deep-link
// bouncing back to the overview. Returns { connectionId }.
export function useConnectRssSilently() {
  const queryClient = useQueryClient();
  return useMutation<RssConnectResult, unknown, void>({
    mutationFn: () => connectRssSilently(),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: integrationsQueryKeys.catalog(),
      }),
    onError: (err) =>
      toast.error(serverErrorMessage(err, "Couldn't connect RSS feeds.")),
  });
}

// Validate / discover a single RSS feed URL for the add-feed wizard (SKE-3995).
// A mutation (not a query): it fires on demand when the user submits a URL, and the
// wizard keeps the returned result in its own step state rather than a cache.
export function useValidateRssFeed() {
  return useMutation<ValidateRssFeedResult, unknown, string>({
    mutationFn: (url: string) => validateRssFeed(url),
  });
}

export function usePauseConnection(connectionId: string) {
  const invalidate = useInvalidateIntegrations();
  return useMutation({
    mutationFn: () => pauseConnection(connectionId),
    onSuccess: () => {
      invalidate(connectionId);
      toast.success("Syncing paused.");
    },
    onError: (err) =>
      toast.error(serverErrorMessage(err, "Couldn't pause syncing.")),
  });
}

export function useResumeConnection(connectionId: string) {
  const invalidate = useInvalidateIntegrations();
  return useMutation({
    mutationFn: () => resumeConnection(connectionId),
    onSuccess: () => {
      invalidate(connectionId);
      toast.success("Syncing resumed.");
    },
    onError: (err) =>
      toast.error(serverErrorMessage(err, "Couldn't resume syncing.")),
  });
}

export function useTriggerSync(connectionId: string) {
  const invalidate = useInvalidateIntegrations();
  return useMutation({
    mutationFn: () => triggerConnectionSync(connectionId),
    onSuccess: (result) => {
      invalidate(connectionId);
      if (result.refreshFailed) {
        toast.warning(REIMPORT_REFRESH_FAILED);
        return;
      }
      toast.success("Sync triggered.");
    },
    onError: (err) =>
      toast.error(serverErrorMessage(err, "Couldn't trigger a sync.")),
  });
}

const REIMPORT_REFRESH_FAILED =
  "Sync started, but we couldn't fetch every file again from the source for a folder being re-imported. We'll keep trying.";

export interface FolderSyncTarget {
  connectionId: string;
  variant: string;
  folderName: string;
}

// "Sync now" from a Library collection header (SKE-3963): one request per folder that
// feeds the collection, each naming its connection and variant. Several folders, even
// on different connections, can feed one collection. Each request settles on its own,
// so one refused folder does not hide the others that started.
export function useTriggerFolderSyncs() {
  const invalidate = useInvalidateIntegrations();
  return useMutation({
    mutationFn: async (targets: FolderSyncTarget[]) => {
      const results = await Promise.allSettled(
        targets.map((target) =>
          triggerConnectionSync(target.connectionId, target.variant)
        )
      );
      const failed = targets.filter(
        (_target, index) => results[index]!.status === "rejected"
      );
      const firstRejection = results.find(
        (result): result is PromiseRejectedResult =>
          result.status === "rejected"
      );
      if (firstRejection && failed.length === targets.length) {
        throw firstRejection.reason;
      }
      const refreshFailed = results.some(
        (result) =>
          result.status === "fulfilled" && result.value.refreshFailed === true
      );
      return { failed, refreshFailed };
    },
    onSettled: (_data, _err, targets) => {
      for (const connectionId of new Set(targets.map((t) => t.connectionId))) {
        invalidate(connectionId);
      }
    },
    onSuccess: ({ failed, refreshFailed }) => {
      if (failed.length > 0) {
        toast.error(
          `Couldn't start the sync for ${failed
            .map((target) => target.folderName)
            .join(", ")}. The other folders are syncing.`
        );
        return;
      }
      if (refreshFailed) {
        toast.warning(REIMPORT_REFRESH_FAILED);
        return;
      }
      toast.success("Sync started.");
    },
    onError: (err) =>
      toast.error(serverErrorMessage(err, "Couldn't start the sync.")),
  });
}

// "Re-import everything" for one library folder (SKE-3963). `in_progress` means a
// re-import of the folder was already running; `refreshFailed` means it re-reads what
// the source last sent instead of asking for the latest files.
export function useReimportLibraryFolder(connectionId: string) {
  const invalidate = useInvalidateIntegrations();
  return useMutation({
    mutationFn: (vars: { variant: string }) =>
      reimportLibraryFolder(connectionId, vars.variant),
    onSuccess: (result) => {
      invalidate(connectionId);
      if (result.state === "in_progress") {
        toast.info("A re-import is already running for this folder.");
        return;
      }
      if (result.refreshFailed) {
        toast.warning(
          "Re-import started, but we couldn't fetch the latest files from the source. Anything added there since the last sync arrives with the next scheduled sync."
        );
        return;
      }
      toast.success("Re-import started. Missing files will come back.");
    },
    onError: (err) =>
      toast.error(serverErrorMessage(err, "Couldn't start the re-import.")),
  });
}

// Re-import one ideas source's rows under the current mapping (E1c). Unlike a plain
// sync trigger this reports what it actually did, so the toast tells the truth when a
// run was already in flight and nothing was queued.
export function useReimportIdeas(connectionId: string) {
  const invalidate = useInvalidateIntegrations();
  return useMutation({
    mutationFn: (vars: { capability: string; variant: string }) =>
      reimportIdeas(connectionId, vars.capability, vars.variant),
    onSuccess: (result) => {
      invalidate(connectionId);
      if (result.state === "in_progress") {
        toast.info("A sync is already running for this source.");
        return;
      }
      // The re-import IS running — the rows already imported are being re-mapped under
      // the current mapping. Only the request for the provider's latest changes
      // failed, so say that and say where those changes will come from, rather than
      // reporting a failure the user can neither see nor act on.
      if (result.refreshFailed) {
        toast.warning(
          "Re-import started, but we couldn't fetch the latest changes from the source. Any changes made there since the last sync will arrive with the next scheduled sync."
        );
        return;
      }
      if (result.state === "never_synced") {
        toast.success("First sync started.");
        return;
      }
      toast.success("Re-import started.");
    },
    onError: (err) =>
      toast.error(serverErrorMessage(err, "Couldn't start the re-import.")),
  });
}

export function useDisconnect() {
  const invalidate = useInvalidateIntegrations();
  return useMutation({
    mutationFn: (connectionId: string) => disconnectConnection(connectionId),
    onSuccess: (_data, connectionId) => {
      invalidate(connectionId);
      toast.success("Disconnected.");
    },
    onError: (err) =>
      toast.error(serverErrorMessage(err, "Couldn't disconnect.")),
  });
}
